import React, { useEffect, useRef, useState } from "react";
import { API } from "@/lib/api";
import { Card, Badge, SectionTitle } from "@/components/ui-hud";
import { useAuth } from "@/context/AuthContext";
import { AEROINTEL_DOMAINS } from "@/data/pjp";
import { Send, Sparkles, User as UserIcon, Users, Globe, ShieldCheck, TrendingUp } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import BackToCockpit from "@/components/BackToCockpit";

const SUGGESTIONS_BY_DOMAIN = {
  general: [
    "Can a G550 depart Aspen at max landing weight?",
    "Explain ETOPS requirements for Part 135.",
    "Best FBO in Geneva for a G700?",
    "What is required for RVSM approval?",
  ],
  aircraft: [
    "G650 vs Falcon 8X — which for KTEB→VHHH?",
    "Phenom 300E W&B example at MZFW",
    "Global 7500 hot & high performance at KASE",
    "Citation Latitude landing distance at high elevation",
  ],
  faa: [
    "Explain 14 CFR §91.175 landing minimums",
    "Part 135 rest requirements for a single-pilot day",
    "What's new in AC 120-76E?",
    "How does an SFAR affect a Part 91 op?",
  ],
  airport: [
    "Best FBO at KTEB after 10pm arrival",
    "Customs procedures at LFPB",
    "Noise procedures KASE — evening arrivals",
    "Crew hotel near EGLF",
  ],
  career: [
    "Typical Gulfstream captain salary Q1 2026",
    "How to break into a Part 135 SIC role",
    "Type rating cost analysis: CE-680 vs Global",
    "Best resume format for corporate flight dept",
  ],
  maintenance: [
    "Common PT6A-114A hot start troubleshooting",
    "G650 thrust reverser Service Bulletin status",
    "APU ECU self-test failure — Falcon 2000",
    "Reliability trends on Rolls BR725",
  ],
  industry: [
    "2026 preowned Phenom 300E value trend",
    "Charter demand outlook Q2 2026",
    "New aircraft — Global 8000 cert timeline",
    "Best-performing broker categories YoY",
  ],
};

export default function AeroIntel() {
  const { user } = useAuth();
  const [domain, setDomain] = useState("general");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const endRef = useRef(null);
  const location = useLocation();
  const autoSentRef = useRef(false);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  // Deep-link support: /aerointel?prompt=... auto-sends the pre-seeded question
  // once (typically from the Job Radar card in the Cockpit).
  useEffect(() => {
    if (autoSentRef.current || !user) return;
    const params = new URLSearchParams(location.search);
    const p = params.get("prompt");
    if (p && p.trim()) {
      autoSentRef.current = true;
      // Slight delay so the initial render can settle
      setTimeout(() => { send(p); }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, location.search]);

  const send = async (text) => {
    if (!text.trim() || busy) return;
    if (!user) return;
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "", domain }]);
    setInput("");

    try {
      const token = localStorage.getItem("pjp_token");
      const res = await fetch(`${API}/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ session_id: sessionId, message: text, domain }),
      });
      if (!res.ok) {
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: `[AeroIntel error: ${res.status} — please try again]`, domain };
          return copy;
        });
        setBusy(false);
        return;
      }
      const sid = res.headers.get("X-Session-Id");
      if (sid && !sessionId) setSessionId(sid);

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        acc += decoder.decode(value, { stream: true });
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: acc, domain };
          return copy;
        });
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "[error contacting AeroIntel]", domain };
        return copy;
      });
    } finally { setBusy(false); }
  };

  const activeDomain = AEROINTEL_DOMAINS.find((d) => d.slug === domain);

  return (
    <div>
      <section className="max-w-7xl mx-auto px-6 pt-6 pb-2">
        <BackToCockpit />
      </section>
      <section className="max-w-7xl mx-auto px-6 pt-4 pb-8">
        <Badge tone="green" pulse>INTELLIGENCE CENTER · ONLINE</Badge>
        <h1 className="mt-4 font-display text-5xl md:text-8xl font-bold tracking-tighter leading-[0.9]">
          <span className="text-orange-hud glow-orange">AeroIntel.</span>
        </h1>
        <p className="mt-4 text-slate-300 max-w-3xl text-lg">
          The Intelligence Center for Private Aviation — AI-powered aviation intelligence enhanced by
          the world's largest professional private aviation community.
        </p>
        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl">
          {[
            { i: Users, n: "26,511+", l: "PROFESSIONALS" },
            { i: Globe, n: "120+", l: "COUNTRIES" },
            { i: Sparkles, n: "6", l: "DOMAINS" },
            { i: ShieldCheck, n: "24/7", l: "ALWAYS ONLINE" },
          ].map((s, i) => (
            <div key={i} className="border-l border-cyan/40 pl-3">
              <s.i className="h-4 w-4 text-cyan mb-1" />
              <div className="font-mono text-2xl text-cyan">{s.n}</div>
              <div className="overline mt-1">{s.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* DOMAIN SELECTOR */}
      <section className="max-w-7xl mx-auto px-6 py-8">
        <div className="overline text-cyan mb-3">SIX DOMAINS OF AVIATION KNOWLEDGE</div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {AEROINTEL_DOMAINS.map((d) => (
            <button
              key={d.slug}
              data-testid={`domain-${d.slug}`}
              onClick={() => setDomain(d.slug)}
              className={`text-left p-4 border transition-colors ${domain === d.slug ? "border-cyan/60 bg-cyan/10" : "border-cyan/10 hover:border-cyan/40"}`}
            >
              <div className="overline text-cyan mb-1">{d.slug.toUpperCase()}</div>
              <div className="font-mono text-sm text-slate-100">{d.label}</div>
            </button>
          ))}
        </div>
      </section>

      {/* CHAT PANEL */}
      <section className="max-w-5xl mx-auto px-6 pb-16">
        {!user && (
          <Card className="mb-6 text-center py-10">
            <div className="overline text-orange-hud mb-2">MEMBERS ONLY</div>
            <p className="text-slate-300 mb-4">Sign in or join PJP to unlock AeroIntel.</p>
            <div className="flex justify-center gap-3">
              <Link to="/login" data-testid="ai-cta-login" className="btn-hud">SIGN IN</Link>
              <Link to="/membership" data-testid="ai-cta-join" className="btn-hud btn-hud-solid">JOIN — $129/YR</Link>
            </div>
          </Card>
        )}

        <Card testId="ai-chat-panel" className="!p-0">
          <div className="border-b border-cyan/10 p-4 flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="overline text-cyan">{activeDomain?.label}</div>
              <div className="font-mono text-xs text-slate-400 mt-1">DOMAIN: {domain.toUpperCase()} · MODEL: GPT-5.2</div>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {activeDomain?.points.slice(0, 4).map((p) => (
                <span key={p} className="font-mono text-[10px] uppercase tracking-widest text-slate-400 border border-cyan/10 px-2 py-1">{p}</span>
              ))}
            </div>
          </div>

          <div className="max-h-[55vh] min-h-[45vh] overflow-y-auto p-6 space-y-6">
            {messages.length === 0 && (
              <div>
                <div className="overline text-cyan mb-4">SUGGESTED QUERIES</div>
                <div className="grid md:grid-cols-2 gap-3">
                  {(SUGGESTIONS_BY_DOMAIN[domain] || SUGGESTIONS_BY_DOMAIN.general).map((s, i) => (
                    <button key={s} data-testid={`ai-suggest-${i}`} onClick={() => send(s)} disabled={!user || busy} className="text-left p-4 border border-cyan/20 hover:border-cyan/50 hover:bg-cyan/5 transition-colors font-mono text-sm text-slate-200">
                      <Sparkles className="h-3.5 w-3.5 inline mr-2 text-cyan" />{s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex gap-3 ${m.role === "user" ? "flex-row-reverse" : ""}`} data-testid={`ai-msg-${i}`}>
                <div className={`h-8 w-8 flex items-center justify-center border ${m.role === "user" ? "border-cyan/50 bg-cyan/10" : "border-orange-hud/50 bg-orange-hud/10"}`}>
                  {m.role === "user" ? <UserIcon className="h-4 w-4 text-cyan" /> : <Sparkles className="h-4 w-4 text-orange-hud" />}
                </div>
                <div className={`flex-1 max-w-[85%] ${m.role === "user" ? "text-right" : ""}`}>
                  <div className="overline mb-1">
                    {m.role === "user" ? "YOU" : `AEROINTEL · ${(m.domain || "general").toUpperCase()}`}
                  </div>
                  <div className={`whitespace-pre-wrap font-body text-sm leading-relaxed ${m.role === "user" ? "text-cyan" : "text-slate-200"}`}>
                    {m.content || <span className="opacity-60 italic">thinking…</span>}
                  </div>
                </div>
              </div>
            ))}
            <div ref={endRef} />
          </div>

          <div className="border-t border-cyan/10 p-4">
            <form onSubmit={(e) => { e.preventDefault(); send(input); }} className="flex gap-2">
              <input
                data-testid="ai-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                disabled={!user || busy}
                placeholder={user ? `Ask AeroIntel about ${activeDomain?.label}…` : "Sign in to chat"}
                className="input-hud flex-1"
              />
              <button data-testid="ai-send" disabled={!user || busy || !input.trim()} className="btn-hud btn-hud-solid">
                <Send className="h-3.5 w-3.5" /> ASK
              </button>
            </form>
          </div>
        </Card>
      </section>

      <section className="border-t border-cyan/10 py-16 bg-obsidian/50">
        <div className="max-w-5xl mx-auto px-6">
          <SectionTitle overline="COMMUNITY INTELLIGENCE" title="The secret sauce." subtitle="This is what separates AeroIntel from every AI platform. Every answer is strengthened by years of knowledge shared inside the PJP community — the wisdom of thousands of experienced aviation professionals, not just a language model." />
          <div className="grid md:grid-cols-3 gap-4">
            {[
              { t: "Powered by 26,511+ professionals", d: "Answers supported by experienced Gulfstream captains, Part 135 operators, A&P mechanics, and aviation executives." },
              { t: "Community discussion available", d: "Every AI answer links to the underlying community threads where members share real-world experience." },
              { t: "Global coverage — 120+ countries", d: "Community knowledge spans 120+ countries and every corner of private and business aviation." },
            ].map((x) => (
              <Card key={x.t}>
                <TrendingUp className="h-5 w-5 text-cyan mb-2" />
                <div className="font-display text-lg font-semibold mb-1">{x.t}</div>
                <p className="text-sm text-slate-400">{x.d}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
